import React, { useState } from "react";
import styles from "styles/BulkUploadModal.module.css";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import DragAndDropUpload from "components/FileUploader/DragAndDropUpload";
import {
  Grid,
  Grid2,
  useTheme,
  Button,
  Box,
  Modal,
  Typography,
  Link,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import checkIcon from "assets/CheckIcon.png";
import fileIcon from "assets/fileIcon.svg";
import { useFileUploadMutation } from "store/slices/apis/fileUploadSlice";
import sampleFile from "assets/SampleFile/sample_excel_file.xlsx";
import { useNavigate, useParams } from "react-router-dom";

const fileTypes = [
  { label: "Milestone", value: "Milestone" },
  { label: "Schedule", value: "Schedule" },
  { label: "Manpower", value: "Manpower" },
  { label: "P&M", value: "P&M" },
  { label: "BOQ", value: "BOQ" },
  { label: "Payment Tracker", value: "PaymentTracker" },
  { label: "Purchase Order", value: "PurchaseOrder" },
];

const FileUploadModal = ({ fileUploadModal, setFileUploadModal, refetch }) => {
  const navigate = useNavigate()
  const { projectId } = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileUpload] = useFileUploadMutation();
  const [fileType, setfileType] = useState("");
  const [fileTypeError, setFileTypeError] = useState("");
  const [isBaseline, setIsBaseline] = useState(false);

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleDrop = (acceptedFiles) => {
    const maxFileSize = 10 * 1024 * 1024;

    if (acceptedFiles[0].size > maxFileSize) {
      openNotification(
        "The selected file size should not be greater than 10MB."
      );
      return;
    }
    setSelectedFile(acceptedFiles[0]);
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setFileTypeError("");
  };

  const handleUpload = async () => {
    if (!fileType) {
      setFileTypeError("File Type is a mandatory field.");
      return;
    }
    setLoading(true);
    try {
      const response = await fileUpload({
        file: selectedFile,
        projectId: projectId || null,
        type: fileType,
        baseline: isBaseline,
      }).unwrap();
      openNotification(
        response?.message ||
        "File uploaded successfully. Processing in background.",
        "success"
      );
      setSelectedFile(null);
      setLoading(false);
      setFileUploadModal(false);
      setIsBaseline(false)
      refetch();
      navigate(`/projects/${projectId}/key-file/pending-approval-file`);
    } catch (error) {
      console.log(error);
      openNotification(`File upload failed. Error: ${error?.data?.message}`, "error");
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFileUploadModal(false);
    setSelectedFile(null);
    setFileTypeError("");
    setfileType("");
    setIsBaseline(false)
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = sampleFile;
    link.download = "sample-excel-file.xlsx";
    link.click();
  };

  return (
    <Modal
      open={fileUploadModal}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") {
          setFileUploadModal(false);
        }
      }}
      className={styles.modalWrapper}
      sx={{
        "& .css-4nmryk-MuiBackdrop-root-MuiModal-backdrop": {
          opacity: "0 !important",
        },
        marginTop: "260px !important",
      }}
    >
      <Box className={styles.modalContent}>
        <Box className={styles.modalHeader}>
          <Typography variant="h6" className={styles.modalTitle}>
            File Upload
          </Typography>
          <IconButton>
            <ClearIcon
              sx={{ fontSize: "25px", opacity: "0.5" }}
              onClick={() => handleModalClose()}
            />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <Grid2 sx={{ flex: "0 0 60%" }}>
            <Autocomplete
              options={fileTypes}
              getOptionLabel={(option) => option?.label}
              onChange={(_, newValue) => {
                setfileType(newValue ? newValue?.value : "");
                setFileTypeError("");
              }}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  paddingTop: "8px !important",
                  paddingBottom: "7px !important",
                  paddingRight: "0px !important",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select File Type"
                  name="fileType"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      backgroundColor: "#fff",
                    },
                    minWidth: "230px",
                    marginTop: "12px",
                  }}
                />
              )}
              ListboxProps={{
                sx: { fontSize: "13px" },
              }}
            />
            {fileTypeError && (
              <Typography
                sx={{
                  color: "#C52A1A",
                  fontSize: "12px",
                  marginTop: "5px",
                }}
              >
                {fileTypeError}
              </Typography>
            )}
          </Grid2>
          <Grid2 sx={{ flex: { xs: "0 0 100%", sm: "0 0 30%" }, display: "flex", alignItems: "center", marginTop: { xs: "0px", sm: '12px' } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isBaseline}
                  onChange={(e) => setIsBaseline(e.target.checked)}
                  sx={{
                    color: theme?.customSettings?.themeColor,
                    "&.Mui-checked": {
                      color: theme?.customSettings?.themeColor,
                    },
                  }}
                />
              }
              label="Baseline"
              sx={{
                fontSize: "14px",
                color: "#252525",
              }}
            />
          </Grid2>
        </Box>

        <Box className={styles.uploadBox}>
          <DragAndDropUpload onDrop={handleDrop} />
        </Box>
        {selectedFile && (
          <>
            <Box className={styles.fileInfo}>
              <Grid2 sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Grid
                  sx={{
                    height: "16px",
                    width: "12px",
                  }}
                >
                  <img src={fileIcon} alt="" />
                </Grid>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#252525",
                  }}
                >
                  {selectedFile.name}
                  {"    "}
                  <span style={{ color: "#696969", marginLeft: "8px" }}>
                    {" "}
                    {(selectedFile.size / 1024).toFixed(2)} kb
                  </span>
                </Typography>
              </Grid2>
              <Grid2
                sx={{
                  height: "16px",
                  width: "16px",
                }}
              >
                <img src={checkIcon} alt="" />
              </Grid2>
            </Box>
          </>
        )}

        <Box className={styles.buttonContainer}>
          <Grid2>
            {selectedFile && (
              <>
                <Button
                  onClick={handleDeleteFile}
                  sx={{
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: 500,
                    color: theme?.customSettings?.themeColor,
                    textTransform: "none",
                  }}
                >
                  Delete attachment(s)
                </Button>
              </>
            )}
          </Grid2>
          <Grid2>
            <Button
              variant="contained"
              sx={{
                backgroundColor: `${theme?.customSettings?.themeColor}`,
                color: "#FFF !important",
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 500,
                padding: "8px 24px",
                textTransform: "none",
                "&.Mui-disabled": {
                  backgroundColor: `${theme?.customSettings?.themeColor}1F`,
                  color: "#FFF !important",
                },
              }}
              loading={loading}
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              Upload
            </Button>
          </Grid2>
        </Box>
      </Box>
    </Modal>
  );
};

export default FileUploadModal;
